import { useTranslation } from "react-i18next";

import { LightbulbIcon, RocketIcon } from "lucide-react";

import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Table, TableBody, TableCell, TableRow } from "simplydo/react/ui/table";

import { UserChip } from "components/lib/Chips";
import type { ChipUser } from "components/lib/Chips/types";

type AdjustableUserListProps<U extends ChipUser> = {
  results: U[];
  searchPlaceholder?: string;
  searchFunction?: (searchValue: string) => void;
  hideSearch?: boolean;
  primaryAction?: React.ReactNode;
  loading?: boolean;
  isAdmin?: (user: U) => boolean;
  actions: (user: U) => React.ReactNode;
  infoCells?: (user: U) => React.ReactNode;
  isGroup?: boolean;
  isTeam?: boolean;
  onClickUser?: (user: U) => void;
};

const AdjustableUserList = <U extends ChipUser>({
  results,
  searchPlaceholder,
  searchFunction,
  hideSearch,
  primaryAction,
  loading,
  isAdmin,
  actions,
  infoCells,
  isGroup,
  isTeam,
  onClickUser,
}: AdjustableUserListProps<U>) => {
  const { t } = useTranslation();

  return (
    <div>
      {hideSearch ? null : (
        <div
          style={{ display: "flex", flexDirection: "row", marginBottom: 10 }}
        >
          {searchFunction && (
            <Input
              style={{ flex: 1, marginRight: 5 }}
              fluid
              loading={!!loading}
              placeholder={searchPlaceholder || "Search..."}
              iconPosition="left"

              onChange={(_e, { value }) => {
                searchFunction(value);
              }}
            />
          )}
          {primaryAction}
        </div>
      )}
      <Table>
        <TableBody>
          {results.map((user) => {
            const userIsAdmin = isAdmin?.(user);
            const actionContent = actions?.(user);
            let label = "Admin";
            if (isGroup) {
              label = "Leader";
            } else if (isTeam) {
              label = `${t("common:capitalise", { key: "generic.idea" })} Owner`;
            }

            return (
              <TableRow key={user._id} onClick={() => onClickUser?.(user)}>
                <TableCell>
                  <UserChip user={user} style={{ marginRight: 5 }} />
                  {userIsAdmin && (
                    <Label size="small" color="purple">
                      <Icon icon={isTeam ? LightbulbIcon : RocketIcon} />
                      {label}
                    </Label>
                  )}
                </TableCell>
                <TableCell> {user.profile?.tagline} </TableCell>
                {infoCells?.(user)}
                {actionContent && (
                  <TableCell textAlign="right" collapsing>
                    {actionContent}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default AdjustableUserList;
